import Splide, {Options} from "@splidejs/splide";
import {useImageModal} from "@features/Shared/Features/ImageModal/Scripts/image-modal";

export default class CarouselBlock {
    readonly carouselSections: NodeListOf<HTMLElement>;
    readonly carouselOptions: Options;
    private ariaTranslations: Record<string, string> | undefined = undefined;

    constructor() {
        this.carouselSections = document.querySelectorAll('.carousel-block__carousel');
        this.carouselOptions = {
            type: 'loop',
            speed: 500,
            width: '100%',
            fixedWidth: '295px',
            start: 0,
            focus: 'center',
            perPage: 3,
            perMove: 1,
            easing: 'ease',
            drag: true,
            flickPower: 150,
            flickMaxPages: 1,
            keyboard: true,
            updateOnMove: true,
            breakpoints: {
                576: { fixedWidth: '424px' },
                768: { fixedWidth: '488px' },
                992: { fixedWidth: '563px' },
            },
            mediaQuery: 'min',
            classes: {
                arrow: 'carousel-block__arrow splide__arrow',
                prev: 'carousel-block__arrow--prev splide__arrow--prev ',
                next: 'carousel-block__arrow--next splide__arrow--next ',
                page: 'carousel-block__pagination-page splide__pagination__page'
            }
        };

        this.init();
    }

    private init(): void {
        if (this.carouselSections.length > 0) {
            this.carouselSections.forEach(carouselSection => {
                this.setUpCarousel(carouselSection);
            });
        }
    }

    private setUpCarousel(carouselSection: HTMLElement): void {
        this.setAriaTranslations(carouselSection);
        this.carouselOptions.i18n = this.ariaTranslations;

        const carousel: Splide = new Splide(carouselSection, this.carouselOptions);

        carousel.mount();

        this.setUpModal(carouselSection, carousel);
    }

    private setAriaTranslations(carousel: HTMLElement): void {
        const ariaTranslationsString = carousel.dataset.ariaText as string;
        const ariaTranslationsArray = ariaTranslationsString.split(', ') as string[];

        this.ariaTranslations = {
            prev: ariaTranslationsArray[0],
            next: ariaTranslationsArray[1],
            first: ariaTranslationsArray[2],
            last: ariaTranslationsArray[3],
            slideX: ariaTranslationsArray[4],
            carousel: ariaTranslationsArray[5],
            select: ariaTranslationsArray[6],
            slide: ariaTranslationsArray[7],
            slideLabel: ariaTranslationsArray[8]
        };
    }

    private setUpModal(carouselSection: HTMLElement, carousel: Splide): void {
        const openModalButtons = carouselSection.querySelectorAll('.carousel-block__open-modal-button') as NodeListOf<HTMLButtonElement>;

        if (openModalButtons.length > 0) {
            openModalButtons.forEach(button => {
                const slide = button.parentElement as HTMLLIElement;
                const image = button.nextElementSibling as HTMLElement;
                const modalElement = slide.querySelector('.image-modal') as HTMLDialogElement;
                const imageModal = useImageModal(modalElement);

                const goToNextSlideListener = (): void => {
                    this.goToNextSlide(carousel);
                };

                const goToPrevSlideListener = (): void => {
                    this.goToPrevSlide(carousel);
                };

                if (slide.classList.contains('is-active')) {
                    button.addEventListener('click', imageModal.open);
                    image.addEventListener('click', imageModal.open);
                }

                if (slide.classList.contains('is-next')) {
                    image.addEventListener('click', goToNextSlideListener);
                }

                if (slide.classList.contains('is-prev')) {
                    image.addEventListener('click', goToPrevSlideListener);
                }

                carousel.on('moved', (): void => {
                    if (slide.classList.contains('is-active')) {
                        button.addEventListener('click', imageModal.open);
                        image.addEventListener('click', imageModal.open);
                    } else {
                        button.removeEventListener('click', imageModal.open);
                        image.removeEventListener('click', imageModal.open);
                    }

                    if (slide.classList.contains('is-next')) {
                        image.addEventListener('click', goToNextSlideListener);
                    } else {
                        image.removeEventListener('click', goToNextSlideListener);
                    }

                    if (slide.classList.contains('is-prev')) {
                        image.addEventListener('click', goToPrevSlideListener);
                    } else {
                        image.removeEventListener('click', goToPrevSlideListener);
                    }
                });

            });
        }
    }

    private goToNextSlide(carousel: Splide): void {
        carousel.go('>');
    }

    private goToPrevSlide(carousel: Splide): void {
        carousel.go('<');
    }
}

new CarouselBlock();
